import { Component, OnInit, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { HttpHelpService } from "../../Data/service/http-help.service";
import Swal from "sweetalert2";
import { Chart, ChartConfiguration, ChartData, ArcElement, DoughnutController, BarElement, BarController, CategoryScale, LinearScale, Tooltip, Legend, LineController, LineElement, PointElement, Filler } from "chart.js";
import DataLabelsPlugin from 'chartjs-plugin-datalabels';

Chart.register(ArcElement, DoughnutController, BarElement, BarController, CategoryScale, LinearScale, Tooltip, Legend, LineController, LineElement, PointElement, Filler, DataLabelsPlugin);
import {
  InvoiceSubscriptionsV2Service,
  InvoiceDashboardStats,
  InvoiceDashboardCurrentPlan,
  InvoiceDashboardRecentInvoice,
  InvoiceDashboardMonthlyTrendPoint,
  InvoiceDashboardStatusDistribution,
  DashboardExpenseBucketItem,
  DashboardFinancialPeriod,
  DashboardFinancialSummary,
} from "src/app/services/invoice-subscriptions-v2.service";
import { DashboardFinancialService } from "src/app/services/dashboard-financial.service";
import { NavService } from "src/app/shared/services/nav.service";

@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"]
})

export class DashboardComponent implements OnInit, OnDestroy {
  curentLang:string="";
  layoutVersion:any;
  user_type = JSON.parse(localStorage.getItem('user_type'));
  officeIdNumber = JSON.parse(localStorage.getItem('office_id'));

  allAnalyticsSubplansData:any=[];
  merchantDashboardStats: InvoiceDashboardStats | null = null;
  merchantCurrentPlan: InvoiceDashboardCurrentPlan | null = null;
  merchantDashboardLoading = false;
  merchantDashboardError = false;

  /** Trend filter: income (P&L total) or receipts (types 400 and 320). */
  selectedTrendFilter: 'income' | 'receipts' = 'income';
  trendFilterLoading = false;
  private trendRequest = 0;

  /** Collapsible blocks inside combined plan + tokens card (defaults: light summary first, heavy sections tucked away). */
  planTokensCardCollapse = { details: true, usage: false, tokens: false };

  /** Mobile financial summary tab */
  mobileFinancialTab: 'current_month' | 'previous_month' | 'year_to_date' = 'current_month';

  /** Expense type period filter */
  selectedExpensePeriod: 'current_month' | 'previous_month' = 'current_month';
  expensePeriodLoading = false;

  readonly expenseBucketColors: Record<string, string> = {
    rent: '#3b82f6',
    purchases: '#14b8a6',
    wages: '#f59e0b',
    fuel: '#a855f7',
    services: '#06b6d4',
    other: '#94a3b8',
  };

  readonly expenseCategoryPalette = [
    '#3b82f6',
    '#14b8a6',
    '#f59e0b',
    '#a855f7',
    '#06b6d4',
    '#ef4444',
    '#22c55e',
    '#eab308',
    '#6366f1',
    '#94a3b8',
  ];

  readonly quickActions: {
    icon: string;
    color: string;
    labelKey: string;
    route: string[];
    queryParams?: Record<string, string | number>;
  }[] = [
    { icon: 'fa-files-o', color: '#a855f7', labelKey: 'dashboard.quickActions.documents', route: ['/dashboard/invoices-v2'] },
    { icon: 'fa-file-text-o', color: '#ef4444', labelKey: 'dashboard.quickActions.createDocument', route: ['/dashboard/invoices-v2/create'] },
    { icon: 'fa-upload', color: '#f97316', labelKey: 'dashboard.quickActions.uploadExpense', route: ['/dashboard/expenses'] },
    { icon: 'fa-user-plus', color: '#22c55e', labelKey: 'dashboard.quickActions.addCustomer', route: ['/dashboard/customers'] },
    { icon: 'fa-cube', color: '#3b82f6', labelKey: 'dashboard.quickActions.addProduct', route: ['/dashboard/products'] },
    { icon: 'fa-building-o', color: '#14b8a6', labelKey: 'dashboard.quickActions.addCompany', route: ['/dashboard/companies'] },
  ];

  togglePlanTokensCardSection(section: "details" | "usage" | "tokens"): void {
    this.planTokensCardCollapse[section] = !this.planTokensCardCollapse[section];
  }
  allActivePlan:any=[];
  subPlansByMerchant:any=[];
  subscribeToPlanDetails:any=[];
  profileData:any = [];
  allOfficeStatisticsData:any=[];
  merchantId:any;
  term:any;
  p: any;

  newOrders = {
    colorClass: "primary",
    icon: "new-order",
    colorClass2: "warning",
    icon2: "customers",
    colorClass3: "success",
    icon4: "invoice",
    colorClass4: "secondary",
    icon3: "profit"
  };

  /* ── Chart.js data ── */

  /** Line chart: invoice trend (6-month breakdown or available periods) */
  trendLineChartData: ChartData<'line'> = { labels: [], datasets: [] };
  trendLineChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 28, left: 8, right: 16 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${this.formatTrendAmount(Number(ctx.parsed.y))}`,
        },
      },
      datalabels: {
        anchor: 'end',
        align: 'top',
        offset: 4,
        clip: false,
        color: '#1e40af',
        font: { weight: 'bold', size: 11 },
        formatter: (value: number) => this.formatTrendAmount(Number(value)),
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
          color: '#94a3b8',
          maxTicksLimit: 6,
          callback: (value) => this.formatTrendAmount(Number(value)),
        },
        grid: { color: 'rgba(148,163,184,0.12)' },
        border: { dash: [4, 4] },
      },
      x: {
        grid: { display: false },
        ticks: { color: '#64748b', font: { size: 11 }, maxRotation: 0, autoSkip: false },
      },
    },
  };

  /** Doughnut: invoice status distribution (payments proxy until backend provides invoice statuses) */
  invoiceStatusChartData: ChartData<'doughnut'> = { labels: [], datasets: [] };
  invoiceStatusChartOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
    },
  };

  /** Doughnut: expenses by type */
  expensesTypeChartData: ChartData<'doughnut'> = { labels: [], datasets: [] };
  expensesTypeChartOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
    },
  };

  /* kept for potential future re-use */
  documentsChartData: ChartData<'doughnut'> = { labels: [], datasets: [] };
  documentsChartOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true, maintainAspectRatio: false, cutout: '65%',
    plugins: { legend: { position: 'bottom', labels: { padding: 16, usePointStyle: true, pointStyle: 'circle' } } },
  };
  paymentsChartData: ChartData<'doughnut'> = { labels: [], datasets: [] };
  paymentsChartOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true, maintainAspectRatio: false, cutout: '65%',
    plugins: { legend: { position: 'bottom', labels: { padding: 16, usePointStyle: true, pointStyle: 'circle' } } },
  };
  activityChartData: ChartData<'bar'> = { labels: [], datasets: [] };
  activityChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true, ticks: { stepSize: 1, precision: 0 } } },
  };
  invoicedMiniChartData: ChartData<'doughnut'> = { labels: [], datasets: [] };
  invoicedMiniChartOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true, maintainAspectRatio: false, resizeDelay: 100, animation: false, cutout: '58%',
    plugins: { legend: { display: false }, tooltip: { enabled: true } },
  };
  growthMiniChartData: ChartData<'bar'> = { labels: [], datasets: [] };
  growthMiniChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true, maintainAspectRatio: false, resizeDelay: 100, animation: false, indexAxis: 'y',
    plugins: { legend: { display: false }, tooltip: { enabled: true } },
    scales: {
      x: { display: false, grid: { display: false } },
      y: { grid: { display: false }, ticks: { color: '#555', font: { size: 12, weight: 'bold' } } },
    },
  };

  constructor(
    private spinner: NgxSpinnerService,
    public translate: TranslateService,
    private service: HttpHelpService,
    private invoiceSubscriptionsV2: InvoiceSubscriptionsV2Service,
    private dashboardFinancial: DashboardFinancialService,
    private navService: NavService,
    private router: Router,
  ) {
    this.curentLang = localStorage.getItem('curentLang');
    this.user_type = JSON.parse(localStorage.getItem('user_type'));
    this.officeIdNumber = JSON.parse(localStorage.getItem('office_id'));
  }

  getAnalyticsSubscribeToPlan() {
    this.spinner.show();
    this.service.getAnalyticsSubscribeToPlan().subscribe({
      next: res => {
        this.allAnalyticsSubplansData = res;
        this.spinner.hide();
      },
      error: () => {
        this.spinner.hide();
      }
    });
  }

  loadMerchantDashboardV2(): void {
    this.merchantDashboardLoading = true;
    this.merchantDashboardStats = null;
    this.merchantCurrentPlan = null;
    this.merchantDashboardError = false;
    const companyId = this.dashboardFinancial.resolveCompanyId();
    this.invoiceSubscriptionsV2.getDashboardStats(null, companyId).subscribe({
      next: (res) => {
        this.applyMerchantDashboardStats(res, companyId);
      },
      error: () => {
        this.merchantDashboardLoading = false;
        this.merchantDashboardError = true;
      },
    });
    this.invoiceSubscriptionsV2.getDashboardCurrentPlan().subscribe({
      next: (res) => {
        this.merchantCurrentPlan = res;
      },
      error: () => {},
    });
  }

  private applyMerchantDashboardStats(res: InvoiceDashboardStats, companyId: number | null): void {
    const needsFinancialSummary = !res.financial_summary && !!companyId;
    const needsExpensesByType = !!companyId && !this.hasUsableExpensesByType(res);

    if (!needsFinancialSummary && !needsExpensesByType) {
      this.finishMerchantDashboardStats(res);
      return;
    }

    if (needsFinancialSummary) {
      this.dashboardFinancial.loadFinancialBlocks(companyId!).subscribe({
        next: (blocks) => {
          this.finishMerchantDashboardStats({ ...res, ...blocks });
        },
        error: () => this.finishMerchantDashboardStats(res),
      });
      return;
    }

    this.dashboardFinancial.loadExpensesByType(companyId!).subscribe({
      next: (expensesByType) => {
        this.finishMerchantDashboardStats({ ...res, expenses_by_type: expensesByType });
      },
      error: () => this.finishMerchantDashboardStats(res),
    });
  }

  private hasUsableExpensesByType(res: InvoiceDashboardStats): boolean {
    return (res.expenses_by_type?.items ?? []).some((item) => item.amount > 0);
  }

  private finishMerchantDashboardStats(stats: InvoiceDashboardStats): void {
    this.merchantDashboardStats = stats;
    this.merchantDashboardLoading = false;
    this.buildCharts(stats);
    this.buildExpensesTypeChart(stats);
    this.loadTrendSeries();
  }

  onExpensePeriodChange(): void {
    const companyId = this.dashboardFinancial.resolveCompanyId();
    if (!companyId) return;
    this.expensePeriodLoading = true;
    this.dashboardFinancial.loadExpensesByType(companyId, this.selectedExpensePeriod).subscribe({
      next: (expensesByType) => {
        this.expensePeriodLoading = false;
        if (!this.merchantDashboardStats) return;
        this.merchantDashboardStats = { ...this.merchantDashboardStats, expenses_by_type: expensesByType };
        this.buildExpensesTypeChart(this.merchantDashboardStats);
      },
      error: () => {
        this.expensePeriodLoading = false;
      },
    });
  }

  onTrendFilterChange(): void {
    this.loadTrendSeries();
  }

  /** ₪1,179 — same integer rounding as the financial summary table. */
  formatTrendAmount(value: number): string {
    const n = Number.isFinite(value) ? value : 0;
    const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
    return `₪${formatted}`;
  }

  private loadTrendSeries(): void {
    const requestId = ++this.trendRequest;
    const companyId = this.dashboardFinancial.resolveCompanyId();

    if (this.selectedTrendFilter === 'receipts') {
      this.trendFilterLoading = true;
      this.invoiceSubscriptionsV2.getDashboardStats(400, companyId).subscribe({
        next: (res) => {
          if (requestId !== this.trendRequest) return;
          this.trendFilterLoading = false;
          this.applyTrendAmounts(this.amountsFromMonthlyTrend(res.activity?.invoices?.monthly_trend ?? []));
        },
        error: () => {
          if (requestId !== this.trendRequest) return;
          this.trendFilterLoading = false;
          this.applyTrendAmounts(new Array(6).fill(0));
        },
      });
      return;
    }

    if (!companyId) {
      this.applyTrendAmounts(new Array(6).fill(0));
      return;
    }

    this.trendFilterLoading = true;
    this.dashboardFinancial.loadIncomeLast6Months(companyId).subscribe({
      next: (amounts) => {
        if (requestId !== this.trendRequest) return;
        this.trendFilterLoading = false;
        this.applyTrendAmounts(this.alignIncomeWithSummary(amounts));
      },
      error: () => {
        if (requestId !== this.trendRequest) return;
        this.trendFilterLoading = false;
        this.applyTrendAmounts(this.alignIncomeWithSummary(new Array(6).fill(0)));
      },
    });
  }

  /** Lock the last two points to the summary table so the chart and the row cannot drift. */
  private alignIncomeWithSummary(amounts: number[]): number[] {
    const next = amounts.slice(0, 6);
    while (next.length < 6) next.push(0);
    const fs = this.financialSummary();
    if (fs?.previous_month) next[4] = Number(fs.previous_month.income ?? 0);
    if (fs?.current_month) next[5] = Number(fs.current_month.income ?? 0);
    return next;
  }

  private amountsFromMonthlyTrend(points: InvoiceDashboardMonthlyTrendPoint[]): number[] {
    const slots = this.last6MonthSlots();
    const zeroBased = points.some((p) => Number(p.month) === 0);
    return slots.map((slot) => {
      const hit = points.find((p) => {
        const month = Number(p.month);
        const normalized = zeroBased ? month + 1 : month;
        return Number(p.year) === slot.year && normalized === slot.month;
      });
      return Number(hit?.total_amount ?? 0);
    });
  }

  private last6MonthSlots(): { year: number; month: number; label: string }[] {
    const isAr = this.curentLang === 'ar';
    const isHe = this.curentLang === 'he';
    const arMonths = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];
    const heMonths = ['ינואר','פברואר','מרץ','אפריל','מאי','יוני','יולי','אוגוסט','ספטמבר','אוקטובר','נובמבר','דצמבר'];
    const enMonths = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const now = new Date();
    const slots: { year: number; month: number; label: string }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const name = isAr ? arMonths[d.getMonth()] : (isHe ? heMonths[d.getMonth()] : enMonths[d.getMonth()]);
      slots.push({ year: d.getFullYear(), month: d.getMonth() + 1, label: `${name} ${d.getFullYear()}` });
    }
    return slots;
  }

  private applyTrendAmounts(amounts: number[]): void {
    const slots = this.last6MonthSlots();
    const trendDataset = {
      borderColor: '#3b82f6',
      backgroundColor: 'rgba(59,130,246,0.10)',
      pointBackgroundColor: '#3b82f6',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 6,
      pointHoverRadius: 9,
      fill: true,
      tension: 0.4,
    };
    this.trendLineChartData = {
      labels: slots.map((slot) => slot.label),
      datasets: [{ ...trendDataset, data: amounts.map((amount) => Number(amount) || 0) }],
    };
  }

  analysisTokensFromDashboard(): { remaining: number; limit: number; percent: number } | null {
    const b = this.merchantDashboardStats?.analysis_tokens;
    if (!b || b.limit == null) return null;
    const percent =
      b.usage_percent != null
        ? Math.round(b.usage_percent)
        : b.limit > 0
          ? Math.round((b.used / b.limit) * 100)
          : 0;
    return { remaining: b.remaining ?? 0, limit: b.limit, percent };
  }

  analysisTokensFromCurrentPlan(): { remaining: number; limit: number; percent: number } | null {
    const u = this.merchantCurrentPlan?.plan_details?.usage;
    if (!u || u.analysis_tokens_limit == null || u.analysis_tokens_remaining == null) return null;
    const lim = u.analysis_tokens_limit;
    const rem = u.analysis_tokens_remaining;
    const used = lim - rem;
    const percent = lim > 0 ? Math.round((used / lim) * 100) : 0;
    return { remaining: rem, limit: lim, percent };
  }

  get analysisTokensCard(): { remaining: number; limit: number; percent: number } | null {
    return this.analysisTokensFromDashboard() || this.analysisTokensFromCurrentPlan();
  }

  /** True when token numbers come from GET /dashboard/stats (not only from current-plan). */
  analysisTokensFromDashboardActive(): boolean {
    return this.analysisTokensFromDashboard() != null;
  }

  /** Used count for display (dashboard preferred). */
  analysisTokensUsedDisplay(): number | null {
    const d = this.merchantDashboardStats?.analysis_tokens;
    if (d && d.used != null) return d.used;
    const tok = this.analysisTokensCard;
    if (!tok) return null;
    return Math.max(0, tok.limit - tok.remaining);
  }

  /** % of analysis token quota still available (for headroom bar in card) */
  analysisTokensRemainingPct(): number {
    const tok = this.analysisTokensCard;
    if (!tok || !tok.limit) return 0;
    return this.clampPct((tok.remaining / tok.limit) * 100);
  }

  private buildCharts(s: InvoiceDashboardStats): void {
    const isAr = this.curentLang === 'ar';
    const isHe = this.curentLang === 'he';

    /* Trend line is loaded separately (income / receipts amounts). */
    const todayCount = s.activity?.invoices?.today_count ?? 0;
    const dist = s.invoice_status_distribution;
    if (dist) {
      this.invoiceStatusChartData = {
        labels: [
          isAr ? 'مؤكدة' : (isHe ? 'מאושרות' : 'Confirmed'),
          isAr ? 'معلقة'  : (isHe ? 'ממתינות'   : 'Pending'),
          isAr ? 'ملغاة'  : (isHe ? 'בוטלו'   : 'Cancelled'),
          isAr ? 'مسودة'  : (isHe ? 'טיוטה'   : 'Draft'),
        ],
        datasets: [{
          data: [dist.confirmed, dist.saved, dist.cancelled, dist.draft],
          backgroundColor: ['#54ba4a', '#ffaa05', '#E62E2D', '#94a3b8'],
          hoverBackgroundColor: ['#48a340', '#e89c04', '#c92726', '#7c8fa3'],
          borderWidth: 3,
          borderColor: '#ffffff',
        }],
      };
    } else {
      const completedCount = s.payments?.completed_count ?? 0;
      const pendingCount   = s.payments?.pending_count ?? 0;
      const failedCount    = s.payments?.failed_count ?? 0;
      this.invoiceStatusChartData = {
        labels: [
          isAr ? 'مؤكدة'  : (isHe ? 'מאושרות' : 'Confirmed'),
          isAr ? 'معلّقة'  : (isHe ? 'ממתינות' : 'Pending'),
          isAr ? 'فاشلة'   : (isHe ? 'נכשלו'   : 'Failed'),
          isAr ? 'اليوم'   : (isHe ? 'היום'     : 'Today'),
        ],
        datasets: [{
          data: [completedCount, pendingCount, failedCount, todayCount],
          backgroundColor: ['#54ba4a', '#ffaa05', '#E62E2D', '#94a3b8'],
          hoverBackgroundColor: ['#48a340', '#e89c04', '#c92726', '#7c8fa3'],
          borderWidth: 3,
          borderColor: '#ffffff',
        }],
      };
    }

    /* ── Legacy charts kept for backward compat ── */
    this.documentsChartData = {
      labels: [isAr ? 'مستخدم' : (isHe ? 'בשימוש' : 'Used'), isAr ? 'متبقي' : (isHe ? 'נותר' : 'Remaining')],
      datasets: [{ data: [s.documents?.used ?? 0, s.documents?.remaining ?? 0], backgroundColor: ['#E62E2D', '#fde0e0'], hoverBackgroundColor: ['#c92726', '#f9c5c5'], borderWidth: 0 }],
    };
    this.paymentsChartData = {
      labels: [isAr ? 'مكتمل' : (isHe ? 'הושלם' : 'Completed'), isAr ? 'معلّق' : (isHe ? 'ממתין' : 'Pending'), isAr ? 'فاشل' : (isHe ? 'נכשל' : 'Failed')],
      datasets: [{ data: [s.payments?.completed_count ?? 0, s.payments?.pending_count ?? 0, s.payments?.failed_count ?? 0], backgroundColor: ['#54ba4a', '#ffaa05', '#E62E2D'], hoverBackgroundColor: ['#48a340', '#e89c04', '#c92726'], borderWidth: 0 }],
    };
    this.activityChartData = {
      labels: [isAr ? 'اليوم' : (isHe ? 'היום' : 'Today'), isAr ? 'هذا الشهر' : (isHe ? 'החודש' : 'This month'), isAr ? 'آخر 30 يومًا' : (isHe ? '30 יום' : 'Last 30 days')],
      datasets: [{ data: [s.activity?.invoices?.today_count ?? 0, s.activity?.invoices?.this_month_count ?? 0, s.activity?.invoices?.last_30_days_count ?? 0], backgroundColor: ['#E62E2D', '#ff6b6b', '#ffaa05'], borderRadius: 6, barThickness: 32 }],
    };
    const mCount = s.activity?.invoices?.this_month_count ?? 0;
    const l30    = s.activity?.invoices?.last_30_days_count ?? 0;
    this.invoicedMiniChartData = {
      labels: [isAr ? 'اليوم' : (isHe ? 'היום' : 'Today'), isAr ? 'هذا الشهر' : (isHe ? 'החודש' : 'This month'), isAr ? 'آخر 30 يومًا' : (isHe ? '30 יום' : 'Last 30 days')],
      datasets: [{ data: [todayCount, mCount, l30], backgroundColor: ['#E62E2D', '#ff6b6b', '#ff9e9e'], hoverBackgroundColor: ['#c92726', '#e05555', '#ff7a7a'], borderWidth: 2, borderColor: '#ffffff' }],
    };
    this.growthMiniChartData = {
      labels: [isAr ? 'عملاء جدد' : (isHe ? 'לקוחות חדשים' : 'New customers'), isAr ? 'مصالح تجارية جديدة' : (isHe ? 'חברות חדשות' : 'New companies'), isAr ? 'عملاء نشطون' : (isHe ? 'פעילים' : 'Active')],
      datasets: [{ data: [s.activity?.customers?.new_last_30_days ?? 0, s.activity?.companies?.new_last_30_days ?? 0, s.activity?.customers?.active_count ?? 0], backgroundColor: ['#E62E2D', '#ffaa05', '#54ba4a'], borderRadius: 8, barThickness: 18 }],
    };
  }

  private buildExpensesTypeChart(s: InvoiceDashboardStats): void {
    const items = (s.expenses_by_type?.items ?? []).filter((i) => i.amount > 0);
    const isAr = this.curentLang === 'ar';
    const isHe = this.curentLang === 'he';
    if (!items.length) {
      this.expensesTypeChartData = { labels: [], datasets: [] };
      return;
    }
    this.expensesTypeChartData = {
      labels: items.map((i) => (isAr ? i.label_ar : (isHe ? i.label_he : i.label_en))),
      datasets: [{
        data: items.map((i) => i.amount),
        backgroundColor: items.map((i, idx) => this.expenseCategoryColor(i.key, idx)),
        hoverBackgroundColor: items.map((i, idx) => this.expenseCategoryColor(i.key, idx)),
        borderWidth: 3,
        borderColor: '#ffffff',
      }],
    };
  }

  expenseCategoryColor(key: string, index: number): string {
    return this.expenseBucketColors[key] ?? this.expenseCategoryPalette[index % this.expenseCategoryPalette.length];
  }

  financialSummary(): DashboardFinancialSummary | null {
    return this.merchantDashboardStats?.financial_summary ?? null;
  }

  mobileFinancialPeriod(): DashboardFinancialPeriod | null {
    const fs = this.financialSummary();
    if (!fs) return null;
    return fs[this.mobileFinancialTab] ?? null;
  }

  desktopFinancialPeriod(key: 'current_month' | 'previous_month' | 'year_to_date'): DashboardFinancialPeriod | null {
    return this.financialSummary()?.[key] ?? null;
  }

  formatPeriodRange(period: DashboardFinancialPeriod | null): string {
    if (!period) return '';
    const fmt = (iso: string) => {
      const [y, m, d] = iso.split('-');
      return `${d}/${m}/${y}`;
    };
    const from = period.start_date <= period.end_date ? period.start_date : period.end_date;
    const to = period.start_date <= period.end_date ? period.end_date : period.start_date;
    return `${fmt(from)} – ${fmt(to)}`;
  }

  formatPeriodRangeShort(period: DashboardFinancialPeriod | null): string {
    if (!period) return '';
    const fmt = (iso: string) => {
      const [y, m, d] = iso.split('-');
      return `${d}/${m}/${y}`;
    };
    if (this.mobileFinancialTab === 'year_to_date') {
      return `${fmt(period.start_date)} ${this.curentLang === 'ar' ? 'إلى الآن' : (this.curentLang === 'he' ? 'עד עכשיו' : 'to Now')}`;
    }
    if (this.mobileFinancialTab === 'current_month') {
      return `${fmt(period.start_date)} ${this.curentLang === 'ar' ? 'إلى الآن' : (this.curentLang === 'he' ? 'עד עכשיו' : 'to Now')}`;
    }
    return `${fmt(period.start_date)} - ${fmt(period.end_date)}`;
  }

  comparisonLabel(key: 'current_month' | 'previous_month' | 'year_to_date'): string {
    if (key === 'year_to_date') {
      return this.curentLang === 'ar'
        ? 'مقارنة بنفس الفترة من العام الماضي'
        : (this.curentLang === 'he' ? 'לעומת אותה תקופה אשתקד' : 'vs same period last year');
    }
    return this.curentLang === 'ar'
      ? 'مقارنة بالشهر السابق'
      : (this.curentLang === 'he' ? 'לעומת החודש הקודם' : 'vs previous month');
  }

  expenseBucketLabel(item: DashboardExpenseBucketItem): string {
    if (this.curentLang === 'ar') return item.label_ar;
    if (this.curentLang === 'he') return item.label_he;
    return item.label_en;
  }

  expenseTypeItems(): DashboardExpenseBucketItem[] {
    return (this.merchantDashboardStats?.expenses_by_type?.items ?? []).filter((i) => i.amount > 0);
  }

  hasExpenseTypeData(): boolean {
    return this.expenseTypeItems().length > 0;
  }

  hasFinancialData(): boolean {
    const fs = this.financialSummary();
    if (!fs) return false;
    const periods = [fs.current_month, fs.previous_month, fs.year_to_date];
    return periods.some((p) => (p?.income ?? 0) > 0 || (p?.expenses ?? 0) > 0);
  }

  hasSelectedCompany(): boolean {
    return this.dashboardFinancial.resolveCompanyId() != null;
  }

  expensesTypeTotal(): number {
    return this.merchantDashboardStats?.expenses_by_type?.total_expenses ?? 0;
  }

  navigateToExpensesReport(categoryIds?: number[]): void {
    const range = this.merchantDashboardStats?.expenses_by_type;
    const queryParams: Record<string, string> = {};
    if (range?.start_date) queryParams['fromDate'] = range.start_date;
    if (range?.end_date) queryParams['toDate'] = range.end_date;
    if (categoryIds?.length) {
      queryParams['category_ids'] = categoryIds.join(',');
    }
    this.router.navigate(['/dashboard/expenses'], { queryParams });
  }

  /** Open the documents list for the selected trend, filtered by date and document type. */
  navigateToTrendInvoiceDetails(): void {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth() - 5, 1);
    const pad = (n: number) => String(n).padStart(2, '0');
    const queryParams: Record<string, string> = {
      fromDate: `${from.getFullYear()}-${pad(from.getMonth() + 1)}-01`,
      toDate: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    };
    if (this.selectedTrendFilter === 'receipts') {
      // 400 = receipt only, 320 = invoice with receipt
      queryParams['invoice_types'] = '400,320';
    } else {
      queryParams['invoice_types'] = '305,320,330';
    }
    const companyId = this.dashboardFinancial.resolveCompanyId();
    if (companyId != null) {
      queryParams['companyId'] = String(companyId);
    }
    this.router.navigate(['/dashboard/invoices-v2'], { queryParams });
  }

  onExpenseTypeClick(item: DashboardExpenseBucketItem): void {
    this.navigateToExpensesReport(item.category_ids?.length ? item.category_ids : undefined);
  }

  setMobileFinancialTab(tab: 'current_month' | 'previous_month' | 'year_to_date'): void {
    this.mobileFinancialTab = tab;
  }
  private formatDashboardPlanName(p: {
    id?: number;
    name_ar?: string | null;
    name_en?: string | null;
    name_he?: string | null;
  } | null | undefined): string {
    if (!p) return "";
    const ar = String(p.name_ar ?? "").trim();
    const en = String(p.name_en ?? "").trim();
    const he = String(p.name_he ?? "").trim();
    let out = "";
    if (this.curentLang === "ar") out = ar || en || he;
    else if (this.curentLang === "he") out = he || en || ar;
    else out = en || ar || he;
    if (out) return out;
    return p.id != null ? `#${p.id}` : "";
  }

  dashboardPlanName(stats: InvoiceDashboardStats | null): string {
    const p = stats?.subscription?.details?.plan;
    return this.formatDashboardPlanName(p) || "—";
  }

  currentPlanName(): string {
    const fromCurrent = this.formatDashboardPlanName(this.merchantCurrentPlan?.plan_details?.plan);
    if (fromCurrent) return fromCurrent;
    const fromStats = this.formatDashboardPlanName(this.merchantDashboardStats?.subscription?.details?.plan);
    return fromStats || "—";
  }

  currentPlanDescription(): string {
    const p = this.merchantCurrentPlan?.plan_details?.plan;
    if (!p) return "";
    if (this.curentLang === "ar") return p.description_ar || p.description_en || "";
    if (this.curentLang === "he") return p.description_he || p.description_en || "";
    return p.description_en || p.description_ar || "";
  }

  /** Clamp 0–100 for progress bar widths */
  clampPct(value: number | null | undefined): number {
    if (value == null || !Number.isFinite(value)) return 0;
    return Math.max(0, Math.min(100, Math.round(value)));
  }

  private periodElapsedPercentFromDates(startIso: string, endIso: string): number | null {
    const start = new Date(startIso).getTime();
    const end = new Date(endIso).getTime();
    const now = Date.now();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
    if (now <= start) return 0;
    if (now >= end) return 100;
    return ((now - start) / (end - start)) * 100;
  }

  /** % of subscription period already elapsed (time progress) */
  planPeriodElapsedPercent(): number | null {
    const pd = this.merchantCurrentPlan?.plan_details;
    if (pd?.start_date && pd?.end_date) {
      return this.periodElapsedPercentFromDates(pd.start_date, pd.end_date);
    }
    const det = this.merchantDashboardStats?.subscription?.details;
    if (det?.start_date && det?.end_date) {
      return this.periodElapsedPercentFromDates(det.start_date, det.end_date);
    }
    return null;
  }

  planDaysRemainingDisplay(): number | null {
    const pd = this.merchantCurrentPlan?.plan_details;
    if (pd && pd.days_remaining != null) return pd.days_remaining;
    const dr = this.merchantDashboardStats?.subscription?.days_remaining;
    return dr != null ? dr : null;
  }

  /** % of document quota still available (headroom) */
  planDocumentsRemainingPct(): number | null {
    const u = this.merchantCurrentPlan?.plan_details?.usage;
    if (u?.documents_limit != null && u.documents_limit > 0) {
      const rem =
        u.documents_remaining != null
          ? u.documents_remaining
          : Math.max(0, u.documents_limit - (u.documents_used ?? 0));
      return (rem / u.documents_limit) * 100;
    }
    const d = this.merchantDashboardStats?.documents;
    if (d?.limit != null && d.limit > 0) {
      return ((d.remaining ?? 0) / d.limit) * 100;
    }
    return null;
  }

  planDocumentsUsedPct(): number | null {
    const u = this.merchantCurrentPlan?.plan_details?.usage;
    if (u?.documents_limit != null && u.documents_limit > 0) {
      return ((u.documents_used ?? 0) / u.documents_limit) * 100;
    }
    const p = this.merchantDashboardStats?.documents?.usage_percent;
    return p != null ? p : null;
  }

  /** % of company quota still available */
  planCompaniesRemainingPct(): number | null {
    const u = this.merchantCurrentPlan?.plan_details?.usage;
    if (u?.companies_limit != null && u.companies_limit > 0) {
      const rem =
        u.companies_remaining != null
          ? u.companies_remaining
          : Math.max(0, u.companies_limit - (u.companies_used ?? 0));
      return (rem / u.companies_limit) * 100;
    }
    return null;
  }

  planCompaniesUsedPct(): number | null {
    const u = this.merchantCurrentPlan?.plan_details?.usage;
    if (u?.companies_limit != null && u.companies_limit > 0) {
      return ((u.companies_used ?? 0) / u.companies_limit) * 100;
    }
    return null;
  }

  planDocumentsRemainingCount(): number | null {
    const u = this.merchantCurrentPlan?.plan_details?.usage;
    if (u?.documents_limit != null) {
      if (u.documents_remaining != null) return u.documents_remaining;
      return Math.max(0, u.documents_limit - (u.documents_used ?? 0));
    }
    const d = this.merchantDashboardStats?.documents;
    return d?.remaining ?? null;
  }

  planCompaniesRemainingCount(): number | null {
    const u = this.merchantCurrentPlan?.plan_details?.usage;
    if (u?.companies_limit != null) {
      if (u.companies_remaining != null) return u.companies_remaining;
      return Math.max(0, u.companies_limit - (u.companies_used ?? 0));
    }
    return null;
  }

  /* ── Period card date helpers ── */

  currentMonthStartFormatted(): string {
    const now = new Date();
    const d = String(1).padStart(2, '0');
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const y = now.getFullYear();
    return `${d}/${m}/${y}`;
  }

  last30DaysRangeFormatted(): string {
    const end   = new Date();
    const start = new Date(end.getTime() - 29 * 24 * 60 * 60 * 1000);
    const fmt   = (d: Date) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
    return `${fmt(start)} - ${fmt(end)}`;
  }

  /* ── Payment percentage helpers ── */

  private paymentsTotalCount(): number {
    return (this.merchantDashboardStats?.payments?.completed_count ?? 0)
         + (this.merchantDashboardStats?.payments?.pending_count   ?? 0)
         + (this.merchantDashboardStats?.payments?.failed_count    ?? 0);
  }

  paymentsCompletedPct(): number {
    const t = this.paymentsTotalCount();
    return t ? Math.round(((this.merchantDashboardStats?.payments?.completed_count ?? 0) / t) * 100) : 0;
  }

  paymentsPendingPct(): number {
    const t = this.paymentsTotalCount();
    return t ? Math.round(((this.merchantDashboardStats?.payments?.pending_count ?? 0) / t) * 100) : 0;
  }

  paymentsFailedPct(): number {
    const t = this.paymentsTotalCount();
    return t ? Math.round(((this.merchantDashboardStats?.payments?.failed_count ?? 0) / t) * 100) : 0;
  }

  /* ── Invoice status distribution helpers ── */

  private invoiceStatusTotal(): number {
    const dist = this.merchantDashboardStats?.invoice_status_distribution;
    if (dist) return dist.total || (dist.confirmed + dist.cancelled + dist.saved + dist.draft);
    return (this.merchantDashboardStats?.payments?.completed_count ?? 0)
         + (this.merchantDashboardStats?.payments?.pending_count   ?? 0)
         + (this.merchantDashboardStats?.payments?.failed_count    ?? 0)
         + (this.merchantDashboardStats?.activity?.invoices?.today_count ?? 0);
  }

  invoiceStatusConfirmedCount(): number  { return this.merchantDashboardStats?.invoice_status_distribution?.confirmed ?? this.merchantDashboardStats?.payments?.completed_count ?? 0; }
  invoiceStatusCancelledCount(): number  { return this.merchantDashboardStats?.invoice_status_distribution?.cancelled ?? this.merchantDashboardStats?.payments?.failed_count    ?? 0; }
  invoiceStatusSavedCount(): number      { return this.merchantDashboardStats?.invoice_status_distribution?.saved    ?? this.merchantDashboardStats?.payments?.pending_count   ?? 0; }
  invoiceStatusDraftCount(): number      { return this.merchantDashboardStats?.invoice_status_distribution?.draft    ?? this.merchantDashboardStats?.activity?.invoices?.today_count ?? 0; }

  invoiceStatusPendingLabel(): string {
    return this.curentLang === 'ar' ? 'معلقة' : (this.curentLang === 'he' ? 'ממתינות' : 'Pending');
  }

  invoiceStatusConfirmedPct(): number { const t = this.invoiceStatusTotal(); return t ? Math.round((this.invoiceStatusConfirmedCount() / t) * 100) : 0; }
  invoiceStatusCancelledPct(): number { const t = this.invoiceStatusTotal(); return t ? Math.round((this.invoiceStatusCancelledCount() / t) * 100) : 0; }
  invoiceStatusSavedPct(): number     { const t = this.invoiceStatusTotal(); return t ? Math.round((this.invoiceStatusSavedCount()     / t) * 100) : 0; }
  invoiceStatusDraftPct(): number     { const t = this.invoiceStatusTotal(); return t ? Math.round((this.invoiceStatusDraftCount()     / t) * 100) : 0; }

  invoiceStatusTotalCount(): number   { return this.merchantDashboardStats?.invoice_status_distribution?.total ?? this.merchantDashboardStats?.activity?.invoices?.total_count ?? 0; }

  /* ── Period cards date helpers ── */

  currentYearStartFormatted(): string {
    const y = new Date().getFullYear();
    return `01/01/${y}`;
  }

  previousMonthRangeFormatted(): string {
    const now = new Date();
    const firstDayOfPrev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDayOfPrev  = new Date(now.getFullYear(), now.getMonth(), 0);
    const fmt = (d: Date) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
    return `${fmt(firstDayOfPrev)} - ${fmt(lastDayOfPrev)}`;
  }

  /* ── Growth badge helpers ── */

  growthLabel(pct: number | null | undefined): string {
    if (pct == null) return '';
    const sign = pct >= 0 ? '↑' : '↓';
    return `${sign} ${Math.abs(pct).toFixed(1)}%`;
  }

  growthClass(pct: number | null | undefined): string {
    if (pct == null) return 'dash-growth-badge--neutral';
    return pct >= 0 ? 'dash-growth-badge--up' : 'dash-growth-badge--down';
  }

  /* ── Recent invoice helpers ── */

  invoiceTypeLabel(inv: InvoiceDashboardRecentInvoice): string {
    if (!inv?.invoice_type_info) return '';
    if (this.curentLang === 'ar') return inv.invoice_type_info.name_ar;
    if (this.curentLang === 'he') return inv.invoice_type_info.name_he;
    return inv.invoice_type_info.name_en;
  }

  /** CSS class for the invoice-type badge pill */
  invoiceTypeBadgeClass(type: number): string {
    switch (type) {
      case 305: return 'inv-badge inv-badge--tax';
      case 320: return 'inv-badge inv-badge--receipt';
      case 330: return 'inv-badge inv-badge--reverse';
      case 332: return 'inv-badge inv-badge--financial';
      case 400: return 'inv-badge inv-badge--receipt-only';
      default:  return 'inv-badge inv-badge--default';
    }
  }

  copyDashboardPaymentLink(url: string): void {
    if (!url) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        Swal.fire({
          icon: "success",
          title: this.curentLang === "ar" ? "تم نسخ رابط الدفع" :
            (this.curentLang === "en" ? "Payment link copied" : "קישור התשלום הועתק"),
        });
      }).catch(() => this.copyDashboardPaymentLinkFallback(url));
      return;
    }
    this.copyDashboardPaymentLinkFallback(url);
  }

  private copyDashboardPaymentLinkFallback(link: string): void {
    const textarea = document.createElement("textarea");
    textarea.value = link;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
    Swal.fire({
      icon: "success",
      title: this.curentLang === "ar" ? "تم نسخ رابط الدفع" :
        (this.curentLang === "en" ? "Payment link copied" : "קישור התשלום הועתק"),
    });
  }

  getAllActivePlans() {
    this.spinner.show();
    this.service.getAllActivePlans().subscribe({
      next: res => {
        this.allActivePlan = res;
        this.spinner.hide();
      },
      error: () => {
        this.spinner.hide();
      }
    });
  }

  getSubscribeDetailsByMerchantId(){
    this.spinner.show()
    this.service.getSubscribeDetailsByMerchantId( ).subscribe(({
      next:res => {
        this.subPlansByMerchant = res;
        this.spinner.hide();
      },
      error:err => {
        
      }
    }))
  }

  getProfileInfo() {
    this.spinner.show();
    this.service.getProfileInfo().subscribe({
        next: res => {
            this.profileData = res;		
            const data = this.profileData?.lookupDetailsUserType?.lookupDetailEnName;
            localStorage.setItem("userType_en", data);

            this.spinner.hide();
            this.navService.requestSidebarLeftMenuRefresh();
        },
        error: error => {
            this.spinner.hide();
        }
    });
}

getOfficeStatistics() {
  this.spinner.show();
  this.service.getOfficeStatistics(this.officeIdNumber).subscribe({
    next: res => {
      this.allOfficeStatisticsData = res;
      
      
      this.spinner.hide();
    },
    error: error => {
      this.spinner.hide();
    }
  });
};

    ngOnInit() {
      this.getProfileInfo();
      const ut = Number(this.user_type);
      if (ut === 100 || ut === 101) {
        this.loadMerchantDashboardV2();
        window.addEventListener('storage', this.onStorageChange);
        window.addEventListener('companyChanged', this.onCompanyChanged);
      } else {
        this.getAnalyticsSubscribeToPlan();
      }
      this.getAllActivePlans();
      if (ut === 300 || ut === 301) {
        this.getOfficeStatistics();
      }

      this.layoutVersion = localStorage.getItem("layout_version");
      if (this.layoutVersion == null || this.layoutVersion === undefined) {
          localStorage.setItem("layout_version", "light-only");
          this.layoutVersion = "light-only";
      } else {
          this.layoutVersion = localStorage.getItem("layout_version");
          if (this.layoutVersion === "dark-only") {
              document.body.classList.add("dark-only");
          } else {
              document.body.classList.add("light-only");
          }
      }

      this.merchantId = JSON.parse(localStorage.getItem('merchant_id'));
      this.user_type = JSON.parse(localStorage.getItem('user_type'));
      this.officeIdNumber = JSON.parse(localStorage.getItem('office_id'));
  }

  private onStorageChange = (event: StorageEvent): void => {
    if (event.key === 'companyDefault') {
      this.loadMerchantDashboardV2();
    }
  };

  private onCompanyChanged = (): void => {
    this.loadMerchantDashboardV2();
  };

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.onStorageChange);
    window.removeEventListener('companyChanged', this.onCompanyChanged);
  }
    

}
